import { Autocomplete as MuiAutocomplete, TextField } from '@mui/material'
import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'
import { useMemo } from 'react'

import { AutocompleteProps, AutocompleteValue } from './types'
import { normalizeAutocompleteOptions } from './utils'

export function Autocomplete<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = AutocompleteValue,
>(props: AutocompleteProps<TargetField, State, AcceptedFormValue>) {
  const {
    label,
    placeholder,
    helperText,
    options,
    validateOn = 'change',
    testId,
    freeSolo = false,
    disabled = false,
    ...rest
  } = props

  const { fieldInfo } = useTidalFormBridge<
    AutocompleteValue,
    TargetField,
    State,
    AcceptedFormValue
  >({
    validateOn,
    ...rest,
  })

  // Convert options to array format if they're provided as a record
  const normalizedOptions = useMemo(() => normalizeAutocompleteOptions(options), [options])

  const selectedOption = useMemo(() => {
    return normalizedOptions.find(option => option.value === fieldInfo.value) || null
  }, [fieldInfo.value, normalizedOptions])

  return (
    <MuiAutocomplete
      value={selectedOption || undefined}
      onChange={(_, newValue) => {
        // Handle both object selection and free text input
        const value = typeof newValue === 'string' ? newValue : newValue?.value || ''
        fieldInfo.onChange(value as string)
      }}
      onBlur={fieldInfo.onBlur}
      options={normalizedOptions}
      freeSolo={freeSolo}
      disabled={disabled || fieldInfo.fieldDisabled}
      disableClearable
      getOptionLabel={option => {
        // Handle both string (freeSolo) and option object
        if (typeof option === 'string') {
          return option
        }
        return option?.label || ''
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={fieldInfo.fieldHasError}
          helperText={fieldInfo.errorOrHelperText || helperText}
          data-testid={testId}
          fullWidth
        />
      )}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
    />
  )
}
